/*
  localization-lazy.js — fills the header country pickers on demand.

  The header renders <ul data-lazy-country-list> EMPTY (snippets/country-localization-lazy.liquid).
  On the first sign of intent we fetch the list once, via the Section Rendering API
  (sections/api-localization-country-list.liquid), and copy its <li> items into every empty list:
    - pointerover / focusin / touchstart on a <localization-form> that contains an empty list
      (desktop hover-intent, keyboard focus, mobile tap)
    - click on such a picker (fallback if no earlier event fired)
    - click on the mobile menu-drawer summary (#Details-menu-drawer-container > summary)
  The list HTML is cached in memory and in sessionStorage (key includes language, current country
  and country count), so it is fetched at most once per session.

  Behaviour once the list is present is the stock <localization-form> (assets/localization-form.js):
  its constructor binds click handlers to the <a> items it finds; for lists filled AFTER the element
  was constructed we bind the element's own onItemClick to the new items (no duplicate binding: if the
  element is not upgraded yet, its constructor will find the items itself).
*/
(function () {
  'use strict';

  if (window.__localizationLazyInit) return;
  window.__localizationLazyInit = true;

  var LIST_SELECTOR = 'ul[data-lazy-country-list]';
  var STORAGE_PREFIX = 'lazyCountryList:v1:';
  var memory = {}; // cacheKey -> items HTML
  var inflight = {}; // cacheKey -> Promise<string>

  function emptyLists() {
    return Array.prototype.filter.call(document.querySelectorAll(LIST_SELECTOR), function (ul) {
      return !ul.hasAttribute('data-lazy-loaded');
    });
  }

  function readStorage(key) {
    try {
      return window.sessionStorage.getItem(STORAGE_PREFIX + key);
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, html) {
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + key, html);
    } catch (e) {
      /* quota / private mode: memory cache still works */
    }
  }

  function load(ul) {
    var key = ul.getAttribute('data-cache-key') || 'default';
    if (memory[key]) return Promise.resolve(memory[key]);

    var stored = readStorage(key);
    if (stored) {
      memory[key] = stored;
      return Promise.resolve(stored);
    }
    if (inflight[key]) return inflight[key];

    var url = ul.getAttribute('data-url');
    if (!url) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      url = root + '?section_id=api-localization-country-list';
    }

    inflight[key] = fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var tpl = document.createElement('template');
        tpl.innerHTML = text;
        var source = tpl.content.querySelector('[data-lazy-country-source] ul') || tpl.content.querySelector('ul.disclosure__list');
        if (!source || !source.querySelector('a[data-value]')) throw new Error('country list missing in response');
        var html = source.innerHTML;
        memory[key] = html;
        writeStorage(key, html);
        return html;
      })
      .finally(function () {
        delete inflight[key];
      });

    return inflight[key];
  }

  function bindItems(ul) {
    var form = ul.closest('localization-form');
    // Upgraded element: its constructor already ran on the empty list -> bind its own handler.
    if (form && typeof form.onItemClick === 'function') {
      var handler = form.onItemClick.bind(form);
      ul.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', handler);
      });
    }
  }

  function fill(ul, html) {
    if (ul.hasAttribute('data-lazy-loaded')) return;
    ul.innerHTML = html;
    ul.setAttribute('data-lazy-loaded', '');
    ul.removeAttribute('aria-busy');
    bindItems(ul);
  }

  function ensure() {
    var lists = emptyLists();
    if (!lists.length) return;
    lists.forEach(function (ul) {
      ul.setAttribute('aria-busy', 'true');
      load(ul)
        .then(function (html) {
          fill(ul, html);
        })
        .catch(function (error) {
          ul.removeAttribute('aria-busy');
          console.error('[localization-lazy]', error);
        });
    });
  }

  function isPickerTarget(target) {
    if (!target || !target.closest) return false;
    var form = target.closest('localization-form');
    if (form && form.querySelector(LIST_SELECTOR + ':not([data-lazy-loaded])')) return true;
    return !!target.closest('#Details-menu-drawer-container > summary');
  }

  function onIntent(event) {
    if (isPickerTarget(event.target)) ensure();
  }

  ['pointerover', 'focusin', 'touchstart', 'click'].forEach(function (type) {
    document.addEventListener(type, onIntent, { capture: true, passive: true });
  });

  // Theme editor: the header section is re-rendered with empty lists -> refill from cache.
  document.addEventListener('shopify:section:load', function () {
    var lists = emptyLists();
    lists.forEach(function (ul) {
      var key = ul.getAttribute('data-cache-key') || 'default';
      if (memory[key]) fill(ul, memory[key]);
    });
  });
})();
